import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Small decorative dot indicating unread/new state. Decorative by default
 * (aria-hidden); consumers can override with aria-label for standalone use.
 */
const notificationDotVariants = cva('inline-block rounded-full shrink-0', {
  variants: {
    size: {
      sm: 'w-2 h-2', // 8px
      md: 'w-3 h-3', // 12px
    },
    variant: {
      default: 'bg-primary',
      destructive: 'bg-destructive',
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
})

export interface NotificationDotProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof notificationDotVariants> {}

const NotificationDot = React.forwardRef<HTMLSpanElement, NotificationDotProps>(
  ({ className, size, variant, 'aria-hidden': ariaHidden = true, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden={ariaHidden}
      className={cn(notificationDotVariants({ size, variant }), className)}
      {...props}
    />
  ),
)
NotificationDot.displayName = 'NotificationDot'

export { NotificationDot, notificationDotVariants }
